/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { CssBaseline, ThemeProvider } from "@material-ui/core";
import {
    AuthProviderV1,
    AuthRedirectMethodV1,
    DialogProviderV1,
    historyV1,
    NotificationProviderV1,
    starTreeLightV1,
} from "@startree-ui/platform-ui";
import "@startree-ui/platform-ui/assets/styles/fonts.scss";
import "@startree-ui/platform-ui/assets/styles/layout.scss";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { FunctionComponent } from "react";
import { Router } from "react-router-dom";
import { App } from "./app";
import { AnalyticsAndChatProviderWrapper } from "./components/analytics-and-chat-provider-wrapper/analytics-and-chart-provider-wrapper.component";
import { EnvironmentInfoProvider } from "./components/environment-info-provider/environment-info-provider.component";
import { DrawerProviderV1 } from "./components/layout/drawer-provider-v1";
import { WorkspaceProvider } from "./components/workspace/workspace-provider/workspace-provider.component";
import { AppRoute } from "./utils/routes/routes.util";

// Auth provider client ID
const CLIENT_ID = "data-manager-ui";

export const Root: FunctionComponent = () => {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={starTreeLightV1}>
                <CssBaseline />

                {/* App rendered by a router to allow navigation using app bar */}
                <Router history={historyV1}>
                    {/* Note: hierarchy of these providers are important */}
                    <NotificationProviderV1 autoDismissInterval={20_000}>
                        <DialogProviderV1>
                            <DrawerProviderV1>
                                <AuthProviderV1
                                    clientId={CLIENT_ID}
                                    redirectMethod={AuthRedirectMethodV1.Post}
                                    redirectPathBlacklist={[
                                        AppRoute.SIGN_IN,
                                        AppRoute.SIGN_OUT,
                                    ]}
                                >
                                    <AnalyticsAndChatProviderWrapper>
                                        <EnvironmentInfoProvider>
                                            <WorkspaceProvider>
                                                <App />
                                            </WorkspaceProvider>
                                        </EnvironmentInfoProvider>
                                    </AnalyticsAndChatProviderWrapper>
                                </AuthProviderV1>
                            </DrawerProviderV1>
                        </DialogProviderV1>
                    </NotificationProviderV1>
                </Router>
            </ThemeProvider>
        </QueryClientProvider>
    );
};
